import { useCallback, useEffect, useRef, useState, type ElementType } from 'react'
import ReactMarkdown from 'react-markdown'
import { Check, Copy, Eye, FileDown, X } from 'lucide-react'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { useAssistantStore, type AssistantArtifact } from '@/stores/assistant-store'
import { useResizablePanel } from '@/hooks/useResizablePanel'
import { copyToClipboard } from '@/lib/clipboard'
import { exportQuickDraftDocx } from '@/lib/api'
import { countWords } from '@/lib/artifact-preview'
import { renumberCitationMarkers, stripCitationMarkers } from '@/lib/remark-citations'
import { useCitationMarkdown } from '@/hooks/useCitationMarkdown'
import { cn } from '@/lib/utils'

const DEFAULT_WIDTH = 560
const MIN_WIDTH = 400
const MAX_WIDTH = 800

/** Longest passage the user can quote back into the chat — matches the turn contract. */
const MAX_SELECTION_LENGTH = 4000

const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'

interface ArtifactContentProps {
  artifact: AssistantArtifact
  /**
   * Element the title renders as. The sheet passes Radix's `DialogTitle` so the
   * dialog is labelled without a second heading.
   */
  TitleComponent?: ElementType
  /**
   * When given, the header shows a close button. The sheet omits it — its
   * Dialog already renders one.
   */
  onClose?: () => void
}

/**
 * The document itself: header, scrollable Markdown body, grounding line and
 * actions. Shared verbatim by the docked panel and the narrow-screen sheet so
 * the two can never drift.
 */
function ArtifactContent({ artifact, TitleComponent = 'h2', onClose }: ArtifactContentProps) {
  const bodyRef = useRef<HTMLDivElement>(null)
  const [copied, setCopied] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  // Reading view: markers hidden, as the export will read. Local and unpersisted —
  // the default is the working view with clickable citations.
  const [isPreview, setIsPreview] = useState(false)
  const copyTimer = useRef<ReturnType<typeof setTimeout>>(undefined)

  // Same citation machinery as the chat bubble. The document is standalone, so
  // its markers are numbered by first use in the document alone: a draft whose
  // first citation is the turn's 12th library source still reads [L1].
  const isStreaming = artifact.status === 'streaming'
  const { sources, librarySources } = artifact
  const {
    normalizedContent,
    remarkPlugins,
    components,
    citationMapping,
    libraryCitationMapping,
    sourcesKey,
  } = useCitationMarkdown({
    content: artifact.content,
    sources,
    librarySources,
    isPending: isStreaming,
    hideCitations: isPreview,
  })

  useEffect(() => () => clearTimeout(copyTimer.current), [])

  /**
   * Capture what the user highlighted so the next message can quote it. Only
   * selections anchored inside this body count — a highlight in the chat
   * transcript is not a quote of the document.
   */
  const handleMouseUp = useCallback(() => {
    const sel = window.getSelection()
    const text = sel?.toString().trim() ?? ''
    if (text && bodyRef.current && sel && bodyRef.current.contains(sel.anchorNode)) {
      useAssistantStore.getState().setPanelSelection({ text: text.slice(0, MAX_SELECTION_LENGTH) })
    }
  }, [])

  /**
   * The Markdown the user takes away, matching what is on screen: markers
   * renumbered to the displayed [1]/[L1] in the working view, gone in Preview.
   * Mid-stream (no honest numbers yet) and on a document with no sources the
   * screen shows no numbers either, so the markers are stripped.
   */
  const getCopyText = useCallback((): string => {
    const tenderCount = sources?.length ?? 0
    const libraryCount = librarySources?.length ?? 0
    if (isPreview || isStreaming || (tenderCount === 0 && libraryCount === 0)) {
      return stripCitationMarkers(artifact.content)
    }
    return renumberCitationMarkers(
      normalizedContent,
      tenderCount > 0 ? { sourceCount: tenderCount, origToDisplay: citationMapping.origToDisplay } : null,
      libraryCount > 0
        ? { sourceCount: libraryCount, origToDisplay: libraryCitationMapping.origToDisplay }
        : null
    )
  }, [
    artifact.content,
    normalizedContent,
    sources,
    librarySources,
    isPreview,
    isStreaming,
    citationMapping,
    libraryCitationMapping,
  ])

  // v1 copies the Markdown as plain text. Rich (HTML) paste is a follow-up.
  const handleCopy = useCallback(async () => {
    const ok = await copyToClipboard(getCopyText())
    if (!ok) return
    setCopied(true)
    clearTimeout(copyTimer.current)
    copyTimer.current = setTimeout(() => setCopied(false), 2000)
  }, [getCopyText])

  const handleExport = useCallback(async () => {
    const { sessionId } = useAssistantStore.getState()
    if (!sessionId) return
    setIsExporting(true)
    try {
      const { filename, contentBase64 } = await exportQuickDraftDocx(sessionId, artifact.turnId)
      const bytes = Uint8Array.from(atob(contentBase64), (c) => c.charCodeAt(0))
      const url = URL.createObjectURL(new Blob([bytes], { type: DOCX_MIME }))
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = filename
      anchor.click()
      URL.revokeObjectURL(url)
    } catch (error) {
      useAssistantStore.setState({
        error: error instanceof Error ? error.message : 'Failed to export the document',
      })
    } finally {
      setIsExporting(false)
    }
  }, [artifact.turnId])

  // Counts the sources the document actually cites — the distinct citation
  // numbers on screen — not every source the turn fetched. The chat's document
  // chip counts the same way (countCitedSources), so the two always agree.
  // Mid-stream nothing is numbered yet, so the turn's fetched count stands in.
  const groundedCount = isStreaming
    ? artifact.groundedSourceCount
    : citationMapping.origToDisplay.size + libraryCitationMapping.origToDisplay.size
  const groundingLine =
    groundedCount > 0
      ? `Based on ${groundedCount} library source${groundedCount === 1 ? '' : 's'}`
      : 'Not grounded in your library'

  const wordCount = countWords(artifact.content)

  return (
    <>
      {/* Header — pad right when the close button lives on the Dialog instead.
          Copy and Export are icon-only here: this row is the panel's whole
          toolbar, and a label on each would crowd out the title. Preview keeps
          its label, as in the section editor: its pressed wash is what says the
          mode is on. */}
      <div
        className={cn(
          'flex shrink-0 items-center gap-1 border-b border-border py-2 pl-4 pr-2',
          !onClose && 'pr-12'
        )}
      >
        <div className="min-w-0 flex-1 pr-1">
          <TitleComponent className="truncate text-sm font-medium">
            {artifact.title}
          </TitleComponent>
          <p className="mt-0.5 flex items-center gap-1.5 text-meta">
            <span>Draft</span>
            <span aria-hidden="true" className="text-border">
              &middot;
            </span>
            <span>
              {wordCount} {wordCount === 1 ? 'word' : 'words'}
            </span>
          </p>
        </div>

        {/* Same control as the section editor's Preview: muted at rest, the
            active wash when on, and clicking again turns it off. */}
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            'h-8 shrink-0 whitespace-nowrap',
            isPreview ? 'bg-foreground/[0.07] text-foreground font-medium' : 'text-muted-foreground'
          )}
          aria-pressed={isPreview}
          onClick={() => setIsPreview((v) => !v)}
          title="Shows the document without citation markers, as it will be exported"
        >
          <Eye className="h-4 w-4 mr-1.5" />
          Preview
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          aria-label={copied ? 'Copied' : 'Copy'}
          title={copied ? 'Copied' : 'Copy'}
          onClick={() => void handleCopy()}
        >
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          aria-label="Export as .docx"
          title={
            artifact.status === 'complete' ? 'Export as .docx' : 'Available when the draft finishes'
          }
          onClick={() => void handleExport()}
          disabled={artifact.status !== 'complete' || isExporting}
        >
          <FileDown className="h-4 w-4" />
        </Button>
        {onClose && (
          <>
            <div aria-hidden="true" className="mx-1 h-4 w-px shrink-0 bg-border" />
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0"
              aria-label="Close document panel"
              title="Close"
              onClick={onClose}
            >
              <X className="h-4 w-4" />
            </Button>
          </>
        )}
      </div>

      {/* Body — the citation reader's tint exactly (bg-muted/30, see
          document-viewer.tsx), so the two right-hand panels read as one
          surface. Keep them in step: changing one without the other is what
          made this panel look like a different product. */}
      <div
        ref={bodyRef}
        onMouseUp={handleMouseUp}
        className="min-h-0 flex-1 overflow-y-auto bg-muted/30 px-6 py-6"
      >
        <div className="mx-auto max-w-[62ch]">
          <div className="chat-markdown prose prose-sm max-w-none dark:prose-invert text-[15px]">
            <ReactMarkdown
              key={`md-${artifact.turnId}-${sourcesKey}`}
              remarkPlugins={remarkPlugins}
              components={components}
            >
              {normalizedContent}
            </ReactMarkdown>
          </div>
          {artifact.truncated && (
            <p className="mt-3 text-meta">
              Cut short — ask me to continue or tighten it.
            </p>
          )}
        </div>
      </div>

      {/* Footer — the grounding line is the draft's provenance, so it stays
          under the document rather than moving into the chrome. */}
      <div className="flex shrink-0 items-center border-t border-border px-4 py-2">
        <span className="truncate text-meta">{groundingLine}</span>
      </div>
    </>
  )
}

/**
 * Docked quick-write document panel (wide screens).
 *
 * Takes the right slot beside the chat column — the same slot as the citation
 * preview, which the store evicts when a document starts streaming. Width is
 * user-adjustable (drag the left edge, double-click to reset) and persisted.
 */
export function ArtifactPanel() {
  const artifact = useAssistantStore((s) => s.artifact)
  const isOpen = useAssistantStore((s) => s.artifactPanelOpen)
  const closeArtifactPanel = useAssistantStore((s) => s.closeArtifactPanel)

  const { width, isResizing, handleProps } = useResizablePanel({
    defaultWidth: DEFAULT_WIDTH,
    minWidth: MIN_WIDTH,
    maxWidth: MAX_WIDTH,
    storageKey: 'assistant-artifact-panel-width',
    invert: true,
  })

  // No Dialog here, so Esc-to-close is ours to own (mirrors ChatDocumentPanel).
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        closeArtifactPanel()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [closeArtifactPanel])

  if (!artifact || !isOpen) return null

  return (
    <div
      className="relative flex h-full min-h-0 shrink-0 flex-col border-l border-border bg-background"
      style={{ width }}
    >
      {/* Resize handle on the left edge — drag to resize, double-click to reset */}
      <div
        {...handleProps}
        className={cn(
          'absolute left-0 top-0 bottom-0 w-1.5 -ml-0.5 z-10 cursor-col-resize hover:bg-primary/20 active:bg-primary/30 transition-colors',
          isResizing && 'bg-primary/30'
        )}
      />
      <ArtifactContent artifact={artifact} onClose={closeArtifactPanel} />
    </div>
  )
}

interface ArtifactSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * The same document as a modal sheet, for viewports too narrow to dock a panel.
 * The inner flex column is deliberate: DialogContent is a grid, which would win
 * over a `flex` utility and leave the body unable to scroll.
 */
export function ArtifactSheet({ open, onOpenChange }: ArtifactSheetProps) {
  const artifact = useAssistantStore((s) => s.artifact)
  if (!artifact) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl h-[85vh] gap-0 overflow-hidden p-0"
        aria-describedby={undefined}
      >
        <div className="flex h-full min-h-0 flex-col">
          <ArtifactContent artifact={artifact} TitleComponent={DialogTitle} />
        </div>
      </DialogContent>
    </Dialog>
  )
}
